import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type WeldOperatorParameters<TAttribute> = {
    name: string
    attribute: TAttribute | null
    tolerance: NumberLike
}

export const weldOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        attribute: z.nullable(attributeSchema),
        tolerance: numberLike,
    })

function applyWeldOperator<T extends IndexingDim>(attribute: GeometryAttributeData<T>, tolerance: number): GeometryAttributeData<T> {
    const attrRefData = attribute.ref

    const weldedAttrRefData = applyGeometryOperator((attrRef: AttributeRef) => attrRef.weld(tolerance), attrRefData)
    return createGeometryAttributeData(weldedAttrRefData, attribute.dim)
}

export abstract class GeometryWeldOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, WeldOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: WeldOperatorParameters<TAttribute>,
    ): parameters is WeldOperatorParameters<TAttribute> & NonNullableParameters<WeldOperatorParameters<TAttribute>> {
        return parameters.attribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<WeldOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attribute, tolerance} = parameters

        const [attributeDataValid, attributeDataInvalid] = scope.branch(evaluator.evaluate(attribute))
        const [toleranceValid, toleranceInvalid] = scope.branch(evaluator.evaluate(tolerance))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(attributeDataValid, toleranceValid),
            ([attributeData, tolerance]) => applyWeldOperator(attributeData, tolerance),
            "applyWeldOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(attributeDataInvalid, toleranceInvalid)}
    }
}
